import { createFileRoute, Outlet } from '@tanstack/react-router';
import { z } from 'zod/v4';
import { getApiSourceQueryOptions } from '@interxfi/api-client/registry/api-source';
import { getDocsListArtifactsQueryOptions, getDocsListArtifactsRequest } from '@interxfi/api-client/registry/list-artifacts';
import { DocsContextProvider } from '../../features/docs/docs-context';
import { DocsLayout } from '../../features/docs/layout/docs-layout';
import { DEFAULT_UNAUTHENTICATED_ROUTE, MFA_SETUP_ROUTE } from '../../util/routing/defaults';
import type { RouteGuardOptions } from '../../util/routing/guard';
import { getPageHead } from '../../util/routing/meta';

const docsSearchSchema = z.object({
  version: z.string().optional(),
  bundle: z.enum(['api', 'program-api', 'clearing-api', 'techops-api']).optional(),
});

export const Route = createFileRoute('/docs/_layout')({
  staticData: {
    guard: {
      auth: { requireAuthenticated: true, redirectToIfNotMet: DEFAULT_UNAUTHENTICATED_ROUTE, redirectToIfMfaRequirementNotMet: MFA_SETUP_ROUTE },
    },
  } as const satisfies RouteGuardOptions,
  validateSearch: docsSearchSchema,
  beforeLoad: async ({ context, location }) => {
    const { version = 'latest', bundle = 'program-api' } = docsSearchSchema.safeParse(location.search).data || {};
    const apiSourceData = await context.queryClient.ensureQueryData(getApiSourceQueryOptions(version, bundle));

    return {
      bundle,
      version,
      effectiveVersion: apiSourceData?.metadata.version,
      registryVersion: apiSourceData?.metadata?.registryVersion,
      apiSourceData,
      apiGuidesData: apiSourceData?.metadata?.version
        ? await context.queryClient.ensureInfiniteQueryData(
            getDocsListArtifactsQueryOptions(getDocsListArtifactsRequest('guides', apiSourceData.metadata.version)),
          )
        : undefined,
    } as const;
  },
  head: () => getPageHead('Documentation'),
  shouldReload: false,
  component: DocsLayoutComponent,
});

function DocsLayoutComponent() {
  const { bundle = 'program-api', version = 'latest' } = Route.useSearch();

  return (
    <DocsContextProvider bundle={bundle} version={version}>
      <DocsLayout>
        <Outlet />
      </DocsLayout>
    </DocsContextProvider>
  );
}
